/**
 * Two-line button label helpers for the LMS staff-ops header actions.
 *
 * Lives in its own module rather than inside LoanProfilePage.tsx because
 * LoanSummaryDialog.tsx also needs it, and LoanProfilePage imports
 * LoanSummaryDialog — importing back the other way would be a circular import.
 */
import React from "react";

/**
 * Per-button overrides that let a label wrap onto TWO lines inside the shared
 * `components/ui/button.tsx`, which by default cannot do it:
 *   * `whitespace-normal` defeats the base `whitespace-nowrap`,
 *   * `h-auto` defeats `size="sm"`'s fixed `h-9` (the second line would
 *     otherwise overflow a 36px-tall box), with `py-1.5` restoring padding,
 *   * `text-left` + `leading-tight` keep the two stacked lines readable.
 *
 * Applied per call site rather than by editing the Button component: that
 * component is used across the whole admin app, and making every button
 * wrappable would change the layout of screens this has nothing to do with.
 */
export const TWO_LINE_BUTTON_CLASS =
    "flex h-auto items-center gap-2 whitespace-normal py-1.5 text-left leading-tight";

/**
 * A button label split across two lines, so the button stays narrow and all
 * three header actions fit on one row.
 *
 * The wrapper is `flex-col` because the Button's own inner `<span>` is a
 * HORIZONTAL flex container — two plain text nodes (or a `<br/>`) would be laid
 * out as two flex items side by side, i.e. still one line.
 */
export const TwoLineLabel: React.FC<{ top: string; bottom: string }> = ({
    top,
    bottom,
}) => (
    <span className="flex flex-col items-start">
        <span>{top}</span>
        <span>{bottom}</span>
    </span>
);
